import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MedicationRequest, Bundle, MedicationDispense, Medication, Patient } from 'fhir/r4';
import { map, of, catchError } from 'rxjs';
import { TableFilteringService, TableFilters, TableRowModel } from '../shared/table-filtering.service';
import { backendEndPointToken } from '../app.config';
import { LoadingUIEnabled } from '../loading.interceptor';

export interface MedicationRequestRow extends TableRowModel {
  groupIdentifier?: string;
  status: string;
  patientName: string;
  medicationName: string;
  authoredOn?: string;
  dispenseState?: {
    isDispensable: boolean;
    hasDispenseInProgress: boolean;
    dispenseInProgressId: string | null;
    isDispenseCompleted: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MedicationRequestSearchService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly tableFiltering = inject(TableFilteringService) as unknown as TableFilteringService<MedicationRequest, MedicationRequestRow>;
  private readonly silentHttpContext = new HttpContext().set(LoadingUIEnabled, false);

  readonly dataSource = new MatTableDataSource<MedicationRequestRow>();
  readonly loading$ = this.tableFiltering.loading$;
  readonly total$ = this.tableFiltering.total$;
  readonly error$ = this.tableFiltering.error$;

  init(patientId?: string | null): void {
    const defaultQuery = patientId 
      ? `/MedicationRequest?subject=Patient/${patientId}`
      : `/MedicationRequest`;

    this.tableFiltering.init({
      resourceType: 'MedicationRequest',
      defaultQuery,
      dataSource: this.dataSource,
      pageSize: 25,
      baseUrl: this.backendUrl,
      transformEntry: (resource: MedicationRequest) => this.transform(resource),
      httpOptions: { context: this.silentHttpContext }
    });

    // After each page load, we want to batch-fetch side data (dispense status, etc.)
    this.dataSource.connect().subscribe(rows => {
      if (rows && rows.length > 0) {
        this.batchLoadSideData(rows as MedicationRequestRow[]);
      }
    });
  }

  destroy(): void {
    this.tableFiltering.destroy();
    this.dataSource.disconnect();
  }

  search(filters: TableFilters): void {
    this.tableFiltering.search(filters);
  }

  changePage(pageIndex: number): void {
    this.tableFiltering.changePage(pageIndex);
  }

  refresh(): void {
    this.tableFiltering.refreshCurrentPage();
  }

  private transform(resource: MedicationRequest): MedicationRequestRow {
    return {
      id: resource.id ?? '',
      resourceType: 'MedicationRequest',
      rawResource: resource,
      groupIdentifier: resource.groupIdentifier?.value ?? 'N/A',
      status: resource.status,
      patientName: resource.subject?.display ?? 'Loading...',
      medicationName: this.getMedicationName(resource),
      authoredOn: resource.authoredOn || (resource as any).authoredDateTime,
      dispenseState: {
        isDispensable: false,
        hasDispenseInProgress: false,
        dispenseInProgressId: null,
        isDispenseCompleted: false
      }
    };
  }

  private getMedicationName(resource: MedicationRequest): string {
    if (resource.medicationCodeableConcept) {
      return resource.medicationCodeableConcept.text || resource.medicationCodeableConcept.coding?.[0]?.display || 'Unknown Medication';
    }
    if (resource.medicationReference) {
      return resource.medicationReference.display || 'Loading Medication...';
    }
    return 'N/A';
  }

  private batchLoadSideData(rows: MedicationRequestRow[]): void {
    const mrIds = rows.map(r => r.id).filter(Boolean);
    if (!mrIds.length) return;

    // 1. Batch fetch Dispense Status
    this.fetchDispenseStatuses(rows).subscribe(statusMap => {
      rows.forEach(row => {
        if (statusMap.has(row.id)) {
          row.dispenseState = statusMap.get(row.id);
        }
      });
    });

    // 2. Resolve Patient Names (if missing)
    const patientRefs = Array.from(new Set(rows
      .filter(r => {
        const mr = r.rawResource as MedicationRequest;
        return !mr.subject?.display && mr.subject?.reference;
      })
      .map(r => (r.rawResource as MedicationRequest).subject!.reference!)
    ));
    if (patientRefs.length) {
        this.resolvePatientNames(patientRefs, rows);
    }

    // 3. Resolve Medication Details (if reference lacks display)
    const medRefs = Array.from(new Set(rows
        .filter(r => {
          const mr = r.rawResource as MedicationRequest;
          return mr.medicationReference && !mr.medicationReference.display;
        })
        .map(r => (r.rawResource as MedicationRequest).medicationReference!.reference!)
    ));
    if (medRefs.length) {
        this.resolveMedicationNames(medRefs, rows);
    }
  }

  private fetchDispenseStatuses(rows: MedicationRequestRow[]) {
    const mrIds = rows.map(r => r.id);
    const url = `${this.backendUrl}/MedicationDispense?prescription=${mrIds.map(id => `MedicationRequest/${id}`).join(',')}&_count=1000`;
    return this.http.get<Bundle>(url, { context: this.silentHttpContext }).pipe(
      map(bundle => {
        const statusMap = new Map<string, any>();
        const allDispenses = (bundle.entry ?? []).map(e => e.resource as MedicationDispense);

        rows.forEach(row => {
          const mrId = row.id;
          const dispenses = allDispenses.filter(d => 
            (d.authorizingPrescription ?? []).some(ref => ref.reference === `MedicationRequest/${mrId}`)
          );
          
          const mrStatus = ((row.rawResource as MedicationRequest).status ?? '').toLowerCase();
          
          const isDispenseCompleted = dispenses.some(d => d.status === 'completed');
          const inProgress = dispenses.find(d => ['preparation', 'on-hold', 'in-progress'].includes(d.status ?? ''));
          const hasDispenseInProgress = !!inProgress;
          const dispenseInProgressId = inProgress?.id ?? null;
          
          const isDispensable = !['completed', 'cancelled', 'entered-in-error', 'draft'].includes(mrStatus) && !hasDispenseInProgress && !isDispenseCompleted;

          statusMap.set(mrId, {
            isDispensable,
            hasDispenseInProgress,
            dispenseInProgressId,
            isDispenseCompleted
          });
        });
        return statusMap;
      }),
      catchError(() => of(new Map()))
    );
  }

  private resolvePatientNames(refs: string[], rows: MedicationRequestRow[]): void {
      const ids = refs.map(r => r.split('/').pop()).join(',');
      this.http.get<Bundle>(`${this.backendUrl}/Patient?_id=${ids}&_summary=true`, { context: this.silentHttpContext }).subscribe(bundle => {
          const nameMap = new Map<string, string>();
          (bundle.entry ?? []).forEach(e => {
              const p = e.resource as Patient;
              const name = p.name?.[0] ? `${p.name[0].family} ${p.name[0].given?.[0] || ''}` : 'Unknown Patient';
              nameMap.set(`Patient/${p.id}`, name);
          });
          rows.forEach(row => {
              const mr = row.rawResource as MedicationRequest;
              const ref = mr.subject?.reference;
              if (ref && nameMap.has(ref)) {
                  row.patientName = nameMap.get(ref)!;
              }
          });
      });
  }

  private resolveMedicationNames(refs: string[], rows: MedicationRequestRow[]): void {
    const ids = refs.map(r => r.split('/').pop()).join(',');
    this.http.get<Bundle>(`${this.backendUrl}/Medication?_id=${ids}`, { context: this.silentHttpContext }).subscribe(bundle => {
        const nameMap = new Map<string, string>();
        (bundle.entry ?? []).forEach(e => {
            const m = e.resource as Medication;
            const name = m.code?.text || m.code?.coding?.[0]?.display || 'Unknown Medication';
            nameMap.set(`Medication/${m.id}`, name);
        });
        rows.forEach(row => {
            const mr = row.rawResource as MedicationRequest;
            const ref = mr.medicationReference?.reference;
            if (ref && nameMap.has(ref)) {
                row.medicationName = nameMap.get(ref)!;
            }
        });
    });
}
}
